import { SidebarItem } from '@/types/navigation';

/**
 * Rótulo visível de cada bloco de menu.
 *
 * Vive aqui, e não em cada item, para o nome do bloco existir UMA vez — repetir
 * `groupLabel` em todo item é como um bloco acaba com dois nomes diferentes.
 *
 * **Bloco sem entrada aqui não ganha rótulo**, só a linha separadora. É o que
 * mantém o menu de Configurações (`organizacao`, `acesso`, `escalas`,
 * `atendimento`) exatamente como sempre foi: ele usa blocos desde antes de
 * existir rótulo, e ganhar quatro títulos de repente seria uma mudança que
 * ninguém pediu.
 */
export const MENU_GROUP_LABELS: Record<string, string> = {
  gestao: 'Gestão',
  usuario: 'Usuário',
};

/**
 * Diz se o item na posição `index` ABRE um bloco novo — ou seja, se deve haver
 * uma linha separadora ACIMA dele.
 *
 * A lista recebida já vem filtrada por permissão, então "item anterior" é sempre
 * o anterior VISÍVEL: um bloco que perdeu o primeiro item continua ganhando a
 * linha no primeiro item que sobrou, e um bloco que sumiu inteiro não deixa
 * linha órfã. O primeiro item da lista nunca abre bloco (nada a separar).
 *
 * Itens sem `group` não abrem bloco — assim um menu que não usa blocos
 * (CRM, Vendas, Automação…) segue exatamente como antes, sem nenhuma linha.
 */
export const startsMenuGroup = (items: SidebarItem[], index: number): boolean => {
  if (index <= 0) return false;
  const current = items[index];
  const previous = items[index - 1];
  if (!current?.group) return false;
  return current.group !== previous?.group;
};

/**
 * Rótulo a desenhar acima do item, ou `null`.
 *
 * Diferente de `startsMenuGroup`, responde `true` também no índice 0: o
 * PRIMEIRO bloco também tem nome. A linha separadora é que não faz sentido no
 * topo — por isso as duas funções existem separadas em vez de uma só.
 */
export const menuGroupLabelAt = (items: SidebarItem[], index: number): string | null => {
  const current = items[index];
  if (!current?.group) return null;
  if (index > 0 && items[index - 1]?.group === current.group) return null;
  return MENU_GROUP_LABELS[current.group] ?? null;
};

/** `true` quando pelo menos um bloco da lista tem rótulo. */
export const hasLabeledGroups = (items: SidebarItem[]): boolean =>
  items.some((item) => item.group !== undefined && MENU_GROUP_LABELS[item.group] !== undefined);
